import { marketingCaseStudyPages } from "./marketing-case-study-pages";
import { serviceCaseStudyPages } from "./service-case-study-pages";

export const caseStudyPages = [
  {
    slug: "plant-factory-direct",
    client: "Plant Factory Direct",
    title: "How We Migrated Plant Factory Direct from BigCommerce to Shopify",
    description:
      "We migrated Plant Factory Direct to Shopify in 38 days, bringing over 272 products, 490 variants, 1,251 images, and 1,368 custom fields.",
    metaDescription:
      "We migrated Plant Factory Direct from BigCommerce to Shopify, including 272 products, 490 variants, 1,251 images, and 1,368 custom fields.",
    path: "/case-study/plant-factory-direct",
    relatedServices: [
      { href: "/shopify", label: "Shopify Agency" },
      {
        href: "/bigcommerce-to-shopify",
        label: "BigCommerce to Shopify Migration",
      },
      {
        href: "/ecommerce-data-migration-services",
        label: "Ecommerce Data Migration",
      },
      {
        href: "/shopify-support-maintenance",
        label: "Shopify Support and Maintenance",
      },
    ],
    image: {
      src: "/case-studies/plant-factory-direct/plant-factory-direct-shopify-store-after.webp",
      width: 2870,
      height: 1794,
      alt: "Plant Factory Direct Shopify storefront designed and built by Ventura Web Design",
    },
    stats: [
      { value: "38 days", label: "Start to finish" },
      { value: "272", label: "Products added" },
      { value: "490", label: "Product variants" },
      { value: "1,368", label: "Custom fields" },
    ],
    overview: {
      eyebrow: "Overview",
      heading:
        "This complex ecommerce migration left no room for missing data.",
      paragraphs: [
        "Plant Factory Direct sells a large selection of plants online. Its existing BigCommerce store held hundreds of products, variants, images, and detailed plant attributes that customers used to choose the right products.",
        "The company wanted to move to Shopify for a better storefront and a more maintainable ecommerce foundation. The migration had to preserve the useful depth of the old catalog while making the shopping experience clearer and easier to manage.",
      ],
    },
    issue: {
      eyebrow: "The issue",
      heading: "We needed to do more than import the catalog.",
      paragraphs: [
        "The source store contained 272 products, 490 variants, 1,251 product photos, and 1,368 custom fields. Those custom fields included important plant information that did not map directly to Shopify’s standard product structure.",
        "Bulk-pricing rules created another platform difference. Shopify could not reproduce the existing pricing behavior natively, so the new store needed a practical replacement that customers and staff could use from launch day.",
      ],
    },
    solution: {
      eyebrow: "The solution",
      heading:
        "We rebuilt the storefront and translated the catalog for Shopify.",
      paragraphs: [
        "We began by reviewing the existing category structure, product hierarchy, usability, analytics, and search visibility. That gave us a record of what needed to be protected before we changed platforms.",
        "We then designed and configured the Shopify storefront, rebuilt the catalog, converted the BigCommerce custom fields into structured Shopify product data, and recreated the specialized pricing with an app-supported volume-discount solution.",
      ],
      highlights: [
        "We designed and configured the Shopify storefront and primary templates.",
        "We added 272 products, 490 variants, and 1,251 product photos.",
        "We converted 1,368 custom fields into structured Shopify product data.",
        "We implemented volume pricing for behavior Shopify did not support natively.",
        "We completed domain transfer, analytics, Search Console, launch QA, and post-launch testing.",
      ],
    },
    outcome: {
      eyebrow: "The outcome",
      heading: "A cleaner Shopify store launched in 38 days.",
      paragraphs: [
        "We launched Plant Factory Direct on Shopify in 38 days. We preserved the catalog detail shoppers needed and built a cleaner storefront for ongoing ecommerce work.",
        "We delivered the complete product and image catalog, structured plant data, replacement volume pricing, and the operational setup required to sell on Shopify.",
      ],
      followUp:
        "After launch, our work continued through Shopify maintenance, merchandising, catalog updates, SEO, Klaviyo, and paid search. In a later 30-day Google Ads reporting period ending August 25, 2026, the campaigns reported a 511% return on ad spend.",
    },
    services: [
      "Shopify design and development",
      "BigCommerce-to-Shopify migration",
      "Ecommerce data migration",
      "Complex catalog migration",
      "Shopify metafields",
      "Volume-discount configuration",
      "Launch planning and quality assurance",
      "Analytics and Search Console setup",
      "Shopify SEO",
      "Google Ads management",
      "Ongoing Shopify maintenance",
    ],
    cta: {
      heading: "Planning a Shopify migration?",
      description:
        "We can help plan the data, rebuild the storefront, account for platform differences, and support the store through launch.",
      primaryLabel: "Discuss your migration",
      primaryHref: "/contact-us",
    },
  },
  ...marketingCaseStudyPages,
  ...serviceCaseStudyPages,
];

export function getCaseStudyPage(slug) {
  return caseStudyPages.find((caseStudy) => caseStudy.slug === slug) || null;
}
